import * as React from 'react'
import Box from '@mui/material/Box'
import Slider from '@mui/material/Slider'

type TimeGranularity = 'hour' | 'min' | 'sec' | 'minute' | 'second' | 'seconds'

export type TimeSliderType =
    | 'dose'
    | 'readjust'
    | 'mix'
    | 'pump-duration'
    | 'pump-interval'

type TimeSliderProps = {
    time: TimeGranularity
    defaultValue: number
    advancedOptionsState: Record<string, unknown>
    setAdvancedOptionsState: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
    type: TimeSliderType
}

function partKey(time: TimeGranularity): 'hour' | 'minute' | 'seconds' {
    if (time === 'hour') return 'hour'
    if (time === 'min' || time === 'minute') return 'minute'
    return 'seconds'
}

function getNestedValue(
    state: Record<string, unknown>,
    sliderType: TimeSliderType,
    part: 'hour' | 'minute' | 'seconds'
): number {
    const n = (obj: unknown, key: string) => {
        if (!obj || typeof obj !== 'object') return 0
        const v = (obj as Record<string, unknown>)[key]
        return typeof v === 'number' && !Number.isNaN(v) ? v : 0
    }
    switch (sliderType) {
        case 'dose':
            return n(state.time_between_doses, part)
        case 'readjust':
            return n(state.time_before_readjustment, part)
        case 'mix':
            return n(state.mixing_time, part)
        case 'pump-duration':
            return n(state.pump_duration, part)
        case 'pump-interval':
            return n(state.pump_interval, part)
        default:
            return 0
    }
}

function patchNested(
    state: Record<string, unknown>,
    sliderType: TimeSliderType,
    part: 'hour' | 'minute' | 'seconds',
    num: number
): Record<string, unknown> {
    const n = typeof num === 'number' && !Number.isNaN(num) ? num : 0
    const merge = (prev: unknown, patch: Record<string, number>) => ({
        ...(typeof prev === 'object' && prev !== null ? (prev as object) : {}),
        ...patch,
    })
    switch (sliderType) {
        case 'dose':
            return {
                ...state,
                time_between_doses: merge(state.time_between_doses, { [part]: n }),
            }
        case 'readjust':
            return {
                ...state,
                time_before_readjustment: merge(state.time_before_readjustment, { [part]: n }),
            }
        case 'mix':
            return {
                ...state,
                mixing_time: merge(state.mixing_time, { [part]: n }),
            }
        case 'pump-duration':
            return {
                ...state,
                pump_duration: merge(state.pump_duration, { [part]: n }),
            }
        case 'pump-interval':
            return {
                ...state,
                pump_interval: merge(state.pump_interval, { [part]: n }),
            }
        default:
            return state
    }
}

const timeLabel: Record<string, string> = {
    hour: 'Hours',
    min: 'Minutes',
    minute: 'Minutes',
    sec: 'Seconds',
    second: 'Seconds',
    seconds: 'Seconds',
}

export default function TimeSlider({
    time,
    defaultValue: _defaultValue,
    advancedOptionsState,
    setAdvancedOptionsState,
    type,
}: TimeSliderProps) {
    const part = partKey(time)
    const sliderValue = getNestedValue(advancedOptionsState, type, part)

    const handleChange = (_event: Event, newValue: number | number[]) => {
        const raw = Array.isArray(newValue) ? newValue[0] : newValue
        const num = typeof raw === 'number' && !Number.isNaN(raw) ? raw : 0
        setAdvancedOptionsState((prev) =>
            patchNested(prev as Record<string, unknown>, type, part, num)
        )
    }

    const max = part === 'hour' ? 24 : 60
    const step = part === 'hour' ? 1 : 5
    const pumpAllDay = Boolean(advancedOptionsState.pump_all_day)

    return (
        <Box className="min-w-0 flex-1">
            <Slider
                aria-label={`${type} ${part}`}
                value={sliderValue}
                onChange={handleChange}
                step={step}
                marks
                min={0}
                max={max}
                valueLabelDisplay="auto"
                disabled={(type === 'pump-interval' || type === 'pump-duration') && pumpAllDay}
                sx={{
                    color: '#34d399',
                    '& .MuiSlider-thumb': { borderColor: '#059669' },
                    '& .MuiSlider-track': { border: 'none' },
                }}
            />
            <span className="text-xs text-zinc-500">{timeLabel[String(time)] ?? String(time)}</span>
        </Box>
    )
}
